<script setup lang="ts">
import dayjs from "dayjs";
import Modal from "@/ui/components/modal.vue";
import ComplexPriceItemCurveApplicationStatsDTO = Modules.BookingExperts.Data.MasterPriceList.ComplexPriceItemCurveApplicationStatsDTO;
import { onMounted, onUnmounted, watch, ref, computed } from "vue";
import ChartQuickView from "../Chart/ChartQuickView.vue";
import StatField from "../Chart/StatField.vue";
import { useCurveModelStore } from "@/modules/booking_experts/store";
import { colorForFactor } from "@/modules/booking_experts/utils";
import { toastApiResult } from "@/toaster";
import BookingDTO = Modules.BookingExperts.Data.BookingDTO;
import { useApiClient } from "@/api/client";

const api = useApiClient("bookingExperts");
const store = useCurveModelStore();

const visible = defineModel<boolean>("visible");
const { showDateControl = true, booking } = defineProps<{
	/** Show the arrow buttons to navigate the check-in date. */
	showDateControl?: boolean;
	booking: BookingDTO;
}>();
const emits = defineEmits(["day:plus", "day:minus"]);

const curves = ref<ComplexPriceItemCurveApplicationStatsDTO[]>([]);
const curveIndex = ref<number | null>(null);
const currentCurve = computed(() => curves.value[curveIndex.value] ?? null);
const curveCount = computed(
	() => booking?.pricing_model_applied_ids.length || 0
);
const isLoading = ref(true);
const isLoadingStore = ref(false);
const errorMessage = ref("");

watch(
	currentCurve,
	() => {
		isLoadingStore.value = true;
		if (!currentCurve.value) return;
		store.currentCurve = currentCurve.value.applied_curve;
		store.referenceData = currentCurve.value.ref_data;
		store.currentOccupancy = {
			occupancy: currentCurve.value.occupancy,
			bookedOccupancy: currentCurve.value.occupancy_at_measure_date,
			leadDay: Math.max(
				dayjs(currentCurve.value.check_in).endOf("day").diff(dayjs(), "day"),
				0
			),
		};
		isLoadingStore.value = false;
	},
	{ deep: true }
);

const occupancyAtBooking = ref("N/A");

const formattedCheckInDate = computed((): string => {
	if (isLoading.value || !booking?.arrival_dates[0]) return "N/A";
	return dayjs(booking?.arrival_dates[0]).format("MMM D, YYYY");
});

const lengthOfStay = computed((): number => {
	if (!booking.length_of_stays.length) return NaN;
	return booking.length_of_stays.reduce((acc, len) => acc + len, 0);
});

const hasPreviousCurve = computed(() => {
	return !isLoading.value && currentCurve.value && curveIndex.value > 0;
});
const hasNextCurve = computed(() => {
	return (
		!isLoading.value &&
		currentCurve.value &&
		curveIndex.value < curveCount.value - 1
	);
});

async function loadCurves() {
	isLoading.value = true;
	curves.value.length = 0;
	errorMessage.value = "";
	try {
		const promises = Array.from({ length: curveCount.value }, (_, i) => {
			return toastApiResult(
				api.getHistoricPriceModelCurve(booking?.pricing_master_price_list_id, {
					occupancy_measure_date: booking?.reservation_period_date_time,
					"filter[date]": dayjs(booking?.arrival_dates[i]).format(
						"YYYY-MM-DD HH:mm:ss"
					),
					"filter[los]": booking?.length_of_stays[i],
				})
			);
		});

		const responses = await Promise.all(promises);
		for (const response of responses) {
			if (response.isOk()) {
				curves.value.push(response.value);
			}
		}

		if (curves.value.length === 0) {
			const lastResponse = responses[responses.length - 1];
			if (lastResponse.isErr()) {
				errorMessage.value = lastResponse.error.message;
			}
		}

		const response = await toastApiResult(
			api.getRelativeOccupancy(booking?.pricing_master_price_list_id, {
				occupancy_measure_date: booking?.reservation_period_date_time,
				"filter[date]": dayjs(booking?.arrival_dates[0]).format(
					"YYYY-MM-DD HH:mm:ss"
				),
				"filter[los]": booking.length_of_stays.reduce(
					(acc, len) => acc + len,
					0
				),
			})
		);
		if (response.isOk()) {
			const percentage = Math.round(response.value * 1000) / 10;
			occupancyAtBooking.value = percentage.toString();
		}
		curveIndex.value = 0;
		isLoading.value = false;
	} catch (error) {
		console.error("Error fetching curve data:", error);
	}
}

function changeBookingIndex(emit: "day:plus" | "day:minus") {
	isLoading.value = true;
	emits(emit);
	isLoading.value = false;
}

// resets curves when going through bookings
watch(
	() => booking,
	() => {
		loadCurves();
	},
	{ deep: true }
);

onMounted(() => {
	loadCurves();
});
onUnmounted(() => {
	store.currentOccupancy = null;
	store.currentCurve = undefined;
	store.referenceData.y0 = store.referenceData.y1 = [];
});
</script>

<template>
	<modal
		v-model:visible="visible"
		title="Booking Curve"
		icon="fa-solid fa-eye"
		width="600"
	>
		<v-card class="border mb-2" variant="flat">
			<v-card-title class="pa-2 curve-card-header bg-background">
				<div class="curve-title">
					<v-icon
						icon="fa-solid fa-bars"
						size="20"
						color="primary"
						class="mr-2"
					/>
					<span class="text-h6 font-weight-medium">Booking Statistics</span>
				</div>
				<div class="date-section">
					<span class="text-caption text-medium-emphasis mr-2">
						Reservation date:
						{{
							dayjs(booking.reservation_period_date_time).format(
								"MMM D, YYYY HH:mm"
							)
						}}
					</span>
				</div>
			</v-card-title>

			<div class="stats-grid px-4">
				<!-- Applied Pricing Factor -->
				<stat-field
					label="Applied Pricing Factor"
					icon="fa-solid fa-percent"
					:value="booking?.pricing_factor_applied ?? 'N/A'"
					:is-loading="isLoading"
					type="factor"
					:color="colorForFactor(booking?.pricing_factor_applied)"
					:skeleton-width="90"
				/>

				<!-- Occupancy at Booking -->
				<stat-field
					label="Occupancy at Booking"
					icon="fa-solid fa-bed"
					:value="occupancyAtBooking"
					unit="%"
					:is-loading="isLoading"
					type="text"
					:skeleton-width="75"
				/>

				<!-- Arrival Date -->
				<stat-field
					label="Arrival Date"
					icon="fa-solid fa-calendar"
					:value="formattedCheckInDate"
					:is-loading="isLoading"
					type="date"
					:skeleton-width="150"
				>
					<template #date-navigation-left>
						<v-btn
							v-if="showDateControl && !isLoading"
							@click="changeBookingIndex('day:minus')"
							size="25"
							color="primary"
							variant="text"
							class="border"
						>
							<v-icon size="14" icon="fa-light fa-arrow-left" />
						</v-btn>
					</template>
					<template #date-navigation-right>
						<v-btn
							v-if="showDateControl && !isLoading"
							@click="changeBookingIndex('day:plus')"
							size="25"
							color="primary"
							variant="text"
							class="border"
						>
							<v-icon size="14" icon="fa-light fa-arrow-right" />
						</v-btn>
					</template>
				</stat-field>

				<!-- Length of Stay -->
				<stat-field
					label="Length of Stay"
					icon="fa-solid fa-arrows-left-right-to-line"
					:value="lengthOfStay"
					unit="day(s)"
					:is-loading="isLoading"
					type="lead-days"
					:skeleton-width="105"
				/>
			</div>
			<v-chip class="ml-3 mb-3 reference-id-chip" size="small" variant="flat">
				<span>
					<b> Reference ID: </b>
					<span>
						{{ booking?.lodging_booking ?? "N/A" }}
					</span>
				</span>
			</v-chip>
		</v-card>
		<v-card class="border" variant="flat">
			<v-card-title class="pa-2 curve-card-header bg-background">
				<div class="curve-title">
					<v-icon
						icon="fa-solid fa-chart-line"
						size="20"
						color="primary"
						class="mr-2"
					/>
					<span class="text-h6 font-weight-medium">Pricing Models</span>
					<span
						v-if="!isLoading && currentCurve"
						class="text-caption text-medium-emphasis ml-2"
						>{{
							`${dayjs(currentCurve.check_in).format("MMM D, YYYY")}, ${
								currentCurve.los
							} day(s)`
						}}
					</span>
				</div>
				<div v-if="curveCount > 1 && !isLoading">
					<v-btn
						:disabled="!hasPreviousCurve"
						@click="curveIndex--"
						size="25"
						color="primary"
						variant="text"
						base-color="white"
						class="border bg-white"
					>
						<v-icon size="14" icon="fa-light fa-arrow-left" />
					</v-btn>
					<span class="mx-2 text-body-1">
						{{ `${curveIndex + 1}/${curveCount}` }}
					</span>
					<v-btn
						:disabled="!hasNextCurve"
						@click="curveIndex++"
						size="25"
						color="primary"
						variant="text"
						base-color="white"
						class="border bg-white"
					>
						<v-icon size="14" icon="fa-light fa-arrow-right" />
					</v-btn>
				</div>
			</v-card-title>

			<chart-quick-view
				:is-loading="isLoading || isLoadingStore"
				:show-border="false"
				:curve-data="{
					id: booking.pricing_model_original_curve_ids[curveIndex]?.toString(),
					name: booking.pricing_model_applied_names[curveIndex],
				}"
				curve-caption="This data was active at the time of the booking"
				:errorMessage="errorMessage"
			/>
		</v-card>

		<template #actions>
			<v-btn color="primary" @click="visible = false">Close</v-btn>
		</template>
	</modal>
</template>

<style scoped>
.curve-card-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	height: 42px;
}

.curve-title {
	display: flex;
	align-items: center;
}

.stats-grid {
	display: grid;
	grid-template-columns: 1fr auto;
	gap: 4px 32px;
	margin: 8px 0;
}

.stats-grid > * {
	min-height: 32px;
}

.date-section {
	min-height: 20px;
	min-width: 180px;
	display: flex;
	justify-content: flex-end;
}

.reference-id-chip {
	height: 24px;
}
</style>
